import { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  GridItem,
  Heading,
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatNumber,
  StatLabel,
  Badge,
} from '@chakra-ui/react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { LineChart, PieChart } from '@/components/dashboard'
import {
  contentReportsService,
  type ContentPerformanceReport,
  type CategoryAnalysisReport,
  type TagAnalysisReport,
  type MediaLibraryReport,
} from '@/lib/services/contentReportsService'

export default function ContentReportsPage() {
  const colors = useThemeColors()
  const [performanceReport, setPerformanceReport] = useState<ContentPerformanceReport | null>(null)
  const [categoryReport, setCategoryReport] = useState<CategoryAnalysisReport | null>(null)
  const [tagReport, setTagReport] = useState<TagAnalysisReport | null>(null)
  const [mediaReport, setMediaReport] = useState<MediaLibraryReport | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [performance, category, tag, media] = await Promise.all([
          contentReportsService.getPerformanceReport(),
          contentReportsService.getCategoryAnalysis(),
          contentReportsService.getTagAnalysis(),
          contentReportsService.getMediaReport(),
        ])

        setPerformanceReport(performance)
        setCategoryReport(category)
        setTagReport(tag)
        setMediaReport(media)
      } catch (error) {
        console.error('Error fetching content reports:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchReports()
  }, [])

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="auto" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Content Reports
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Analyze content performance, views, and engagement statistics
              </Text>
            </CardHeader>

            <CardBody pb={4} flex="1" overflow="auto" minH={0}>
              <Tabs colorScheme="green">
                <TabList>
                  <Tab>Performance</Tab>
                  <Tab>Categories</Tab>
                  <Tab>Tags</Tab>
                  <Tab>Media Library</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    {performanceReport && (
                      <VStack spacing={6} align="stretch">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Total Pages</StatLabel>
                                <StatNumber>{performanceReport.summary.total_pages}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Published</StatLabel>
                                <StatNumber>{performanceReport.summary.published}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Draft</StatLabel>
                                <StatNumber>{performanceReport.summary.draft}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                        </Grid>

                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                          <GridItem>
                            <PieChart
                              title="Pages by Status"
                              labels={performanceReport.by_status.labels}
                              data={performanceReport.by_status.data}
                              isLoading={isLoading}
                            />
                          </GridItem>
                          <GridItem>
                            <PieChart
                              title="Pages by Author"
                              labels={performanceReport.by_author.labels}
                              data={performanceReport.by_author.data}
                              isLoading={isLoading}
                            />
                          </GridItem>
                          <GridItem>
                            <LineChart
                              title="Publishing Trend"
                              labels={performanceReport.publishing_trend.labels}
                              data={performanceReport.publishing_trend.data}
                              color="#38A169"
                              isLoading={isLoading}
                            />
                          </GridItem>
                          <GridItem>
                            <LineChart
                              title="Content Creation Trend"
                              labels={performanceReport.creation_trend.labels}
                              data={performanceReport.creation_trend.data}
                              color="#3182CE"
                              isLoading={isLoading}
                            />
                          </GridItem>
                        </Grid>
                      </VStack>
                    )}
                  </TabPanel>

                  <TabPanel>
                    {categoryReport && (
                      <VStack spacing={6} align="stretch">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Total Categories</StatLabel>
                                <StatNumber>{categoryReport.summary.total_categories}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Active Categories</StatLabel>
                                <StatNumber>{categoryReport.summary.active_categories}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Total Pages</StatLabel>
                                <StatNumber>{categoryReport.summary.total_pages}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                        </Grid>

                        <Card>
                          <CardHeader>
                            <Heading size="md">Categories</Heading>
                          </CardHeader>
                          <CardBody>
                            <Table variant="simple">
                              <Thead>
                                <Tr>
                                  <Th>Name</Th>
                                  <Th isNumeric>Page Count</Th>
                                  <Th>Status</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {categoryReport.categories.map((category) => (
                                  <Tr key={category.id}>
                                    <Td>{category.name}</Td>
                                    <Td isNumeric>{category.page_count}</Td>
                                    <Td>
                                      <Badge colorScheme={category.active ? 'green' : 'gray'}>
                                        {category.active ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </CardBody>
                        </Card>
                      </VStack>
                    )}
                  </TabPanel>

                  <TabPanel>
                    {tagReport && (
                      <VStack spacing={6} align="stretch">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Total Tags</StatLabel>
                                <StatNumber>{tagReport.summary.total_tags}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Active Tags</StatLabel>
                                <StatNumber>{tagReport.summary.active_tags}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                        </Grid>

                        <Card>
                          <CardHeader>
                            <Heading size="md">Tags</Heading>
                          </CardHeader>
                          <CardBody>
                            <Table variant="simple">
                              <Thead>
                                <Tr>
                                  <Th>Name</Th>
                                  <Th isNumeric>Page Count</Th>
                                  <Th>Color</Th>
                                  <Th>Status</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {tagReport.tags.map((tag) => (
                                  <Tr key={tag.id}>
                                    <Td>{tag.name}</Td>
                                    <Td isNumeric>{tag.page_count}</Td>
                                    <Td>
                                      <Box
                                        w={4}
                                      h={4}
                                        bg={tag.color}
                                        borderRadius="full"
                                        display="inline-block"
                                      />
                                    </Td>
                                    <Td>
                                      <Badge colorScheme={tag.active ? 'green' : 'gray'}>
                                        {tag.active ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </CardBody>
                        </Card>
                      </VStack>
                    )}
                  </TabPanel>

                  <TabPanel>
                    {mediaReport && (
                      <VStack spacing={6} align="stretch">
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Total Files</StatLabel>
                                <StatNumber>{mediaReport.summary.total_files}</StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Total Size</StatLabel>
                                <StatNumber>
                                  {(mediaReport.summary.total_size / 1024 / 1024).toFixed(2)} MB
                                </StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                          <Card>
                            <CardBody>
                              <Stat>
                                <StatLabel>Average Size</StatLabel>
                                <StatNumber>
                                  {(mediaReport.summary.average_size / 1024).toFixed(2)} KB
                                </StatNumber>
                              </Stat>
                            </CardBody>
                          </Card>
                        </Grid>

                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                          <GridItem>
                            <PieChart
                              title="Files by Type"
                              labels={mediaReport.by_type.labels}
                              data={mediaReport.by_type.data}
                              isLoading={isLoading}
                            />
                          </GridItem>
                          <GridItem>
                            <LineChart
                              title="Upload Trend"
                              labels={mediaReport.upload_trend.labels}
                              data={mediaReport.upload_trend.data}
                              color="#805AD5"
                              isLoading={isLoading}
                            />
                          </GridItem>
                        </Grid>
                      </VStack>
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}

